/**
 * Applies the SAME permission filter as the restore path (`layout-envelope.ts`)
 * to a preset's static JSON before it is ever shown — defence in depth
 * alongside the factory-time and restore-time checks (three layers total,
 * not a replacement for either).
 */
import type { IJsonModel } from "flexlayout-react";
import type { PermissionCode } from "../../app/generated/shell-permission-codes";
import type { WorkspaceComponentRegistry } from "../registry/component-registry";
import type { DeviceMode, WorkspaceLayoutEnvelope } from "../types";
import { applyPermissionFilter, applyRegistryWhitelist, CURRENT_SCHEMA_VERSION } from "./layout-envelope";

/** A raw device/model preset — distinct from the ADR-UI-001 `WorkspacePreset` (device mode + adapter-neutral `WorkspaceLayout`) exported from `../types`. */
export interface WorkspaceModelPreset {
  readonly presetId: string;
  readonly deviceMode: DeviceMode;
  readonly model: IJsonModel;
}

export function buildWorkspaceModel(
  preset: WorkspaceModelPreset,
  registry: WorkspaceComponentRegistry,
  canDiscover: (required: readonly PermissionCode[]) => boolean,
): WorkspaceLayoutEnvelope {
  const whitelisted = applyRegistryWhitelist(preset.model, registry);
  const permitted = applyPermissionFilter(whitelisted, registry, canDiscover);
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    presetId: preset.presetId,
    deviceMode: preset.deviceMode,
    model: permitted,
  };
}
